import React from "react";
import PropTypes from "prop-types";
import {
  Box,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import RemoveIcon from "@material-ui/icons/Remove";
import AddIcon from "@material-ui/icons/Add";

import {
  GENDER_OPTIONS,
  roundCounts,
  schemaFor,
  SET_HEIGHT_OPTIONS,
} from "../../../utils/rulesApi";
import { useRuleStyles } from "../styles";

export const MAX_PER_KIND = 11;
export const MAX_TOTAL = 22;

const Stepper = ({ id, label, value, onChange, disabled, max }) => {
  const clamp = (n) => Math.min(max, Math.max(0, n));
  const classes = useRuleStyles();
  const fewer = `Fewer ${label.toLowerCase()}`;
  const more = `More ${label.toLowerCase()}`;
  return (
    <Box>
      <Typography id={`${id}-label`} className={classes.stepperLabel}>
        {label}
      </Typography>
      <Box
        className={classes.stepper}
        role="group"
        aria-labelledby={`${id}-label`}
      >
        <IconButton
          className={classes.iconButton}
          aria-label={fewer}
          disabled={disabled || value <= 0}
          onClick={() => onChange(clamp(value - 1))}
        >
          <RemoveIcon />
        </IconButton>
        <input
          id={id}
          type="number"
          min={0}
          max={max}
          value={value}
          disabled={disabled}
          aria-labelledby={`${id}-label`}
          onChange={(e) => {
            const n = Number(e.target.value);
            onChange(Number.isFinite(n) ? clamp(Math.trunc(n)) : 0);
          }}
        />
        <IconButton
          className={classes.iconButton}
          aria-label={more}
          disabled={disabled || value >= max}
          onClick={() => onChange(clamp(value + 1))}
        >
          <AddIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

Stepper.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  max: PropTypes.number.isRequired,
};

Stepper.defaultProps = { disabled: false };

const KIND_LABEL = { v: "voluntary", o: "optional" };

/** One toggle per round: the default voluntary / optional order the dive sheet starts from. */
const RoundStrip = ({ rounds, onChange, disabled }) => {
  const classes = useRuleStyles();
  return (
    <Box
      className={classes.roundStrip}
      role="group"
      aria-label="Default round order"
    >
      {rounds.split("").map((kind, i) => {
        const other = kind === "v" ? "o" : "v";
        return (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={i} className={classes.roundCell}>
            <span className={classes.roundNumber} aria-hidden="true">
              {i + 1}
            </span>
            <button
              type="button"
              className={`${classes.roundToggle} ${
                kind === "v" ? classes.roundVoluntary : classes.roundOptional
              }`}
              disabled={disabled}
              aria-label={`Round ${i + 1}: ${KIND_LABEL[kind]}. Make it ${
                KIND_LABEL[other]
              }`}
              onClick={() =>
                onChange(rounds.slice(0, i) + other + rounds.slice(i + 1))
              }
            >
              {kind.toUpperCase()}
            </button>
          </Box>
        );
      })}
    </Box>
  );
};

RoundStrip.propTypes = {
  rounds: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

RoundStrip.defaultProps = { disabled: false };

/** New rounds repeat the last round's kind (optional when there is none). */
const resize = (rounds, total) =>
  total <= rounds.length
    ? rounds.slice(0, total)
    : rounds + (rounds.slice(-1) || "o").repeat(total - rounds.length);

/** "Applies when" half of the editor: the lookup tuple and the jump schema. */
const HeaderForm = ({ value, onChange, errors, disabled }) => {
  const classes = useRuleStyles();
  const total = value.rounds.length;
  const { voluntaries, optionals } = roundCounts(value.rounds);
  const schema = schemaFor(value.rounds, value.storedSchema);
  const patch = (changes) => onChange({ ...value, ...changes });

  return (
    <Box>
      <Box className={classes.formGrid}>
        <TextField
          id="rule-set-name"
          label="Rule set name"
          variant="outlined"
          size="small"
          required
          disabled={disabled}
          value={value.ruleName}
          onChange={(e) => patch({ ruleName: e.target.value })}
          inputProps={{ maxLength: 255 }}
          error={Boolean(errors.ruleName)}
          helperText={errors.ruleName || " "}
          style={{ gridColumn: "1 / -1" }}
        />
        <TextField
          id="age-group"
          label="Age group"
          placeholder="e.g. 14-15 or Any"
          variant="outlined"
          size="small"
          required
          disabled={disabled}
          value={value.ageGroup}
          onChange={(e) => patch({ ageGroup: e.target.value })}
          inputProps={{ maxLength: 32 }}
          error={Boolean(errors.ageGroup)}
          helperText={
            errors.ageGroup || "Must match the event's age group text exactly"
          }
        />
        <TextField
          select
          id="gender"
          label="Gender"
          variant="outlined"
          size="small"
          required
          disabled={disabled}
          value={value.gender}
          onChange={(e) => patch({ gender: e.target.value })}
          error={Boolean(errors.gender)}
          helperText={errors.gender || " "}
        >
          {GENDER_OPTIONS.map((g) => (
            <MenuItem key={g} value={g}>
              {g}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          id="height"
          label="Height"
          variant="outlined"
          size="small"
          required
          disabled={disabled}
          value={value.height}
          onChange={(e) => patch({ height: e.target.value })}
          error={Boolean(errors.height)}
          helperText={errors.height || " "}
        >
          {SET_HEIGHT_OPTIONS.map((h) => (
            <MenuItem key={h.value} value={h.value}>
              {h.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          id="code"
          label="Code (sort)"
          variant="outlined"
          size="small"
          required
          disabled={disabled}
          value={value.code}
          onChange={(e) =>
            patch({ code: e.target.value.replace(/[^0-9]/g, "") })
          }
          inputProps={{
            inputMode: "numeric",
            pattern: "[0-9]*",
            maxLength: 10,
          }}
          error={Boolean(errors.code)}
          helperText={
            errors.code ||
            "Digits only; unique among active sets with the same header"
          }
        />
      </Box>
      <Box className={classes.roundOrder}>
        <Typography className={classes.stepperLabel}>
          Default round order
        </Typography>
        <Box className={classes.roundOrderRow}>
          <Stepper
            id="total-dives"
            label="Total dives"
            value={total}
            max={MAX_TOTAL}
            disabled={disabled}
            onChange={(n) => patch({ rounds: resize(value.rounds, n) })}
          />
          <RoundStrip
            rounds={value.rounds}
            disabled={disabled}
            onChange={(rounds) => patch({ rounds })}
          />
        </Box>
        <Typography
          className={classes.inlineHelp}
          color={errors.total ? "error" : "initial"}
          role={errors.total ? "alert" : undefined}
        >
          {errors.total ||
            "Click a round to switch it between V and O. This only pre-fills the dive sheet; " +
              "divers can change it, so use rules to restrict which rounds hold voluntaries."}
        </Typography>
      </Box>
      <Box className={classes.schemaNote} aria-live="polite">
        Schema <span className={classes.mono}>{schema}</span> — {total} dive
        {total === 1 ? "" : "s"}
        {total > 0 &&
          `, ${voluntaries} voluntar${
            voluntaries === 1 ? "y" : "ies"
          } and ${optionals} optional${optionals === 1 ? "" : "s"}`}
        . The <strong>number of dives</strong> rule is kept in sync
        automatically.
      </Box>
    </Box>
  );
};

HeaderForm.propTypes = {
  value: PropTypes.shape({
    ruleName: PropTypes.string,
    ageGroup: PropTypes.string,
    gender: PropTypes.string,
    height: PropTypes.string,
    rounds: PropTypes.string,
    storedSchema: PropTypes.string,
    code: PropTypes.string,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
  disabled: PropTypes.bool,
};

HeaderForm.defaultProps = { errors: {}, disabled: false };

export default HeaderForm;
